import { format, formatDistanceToNow } from "date-fns";

export const formatUSD = (amount: number | string, decimals = 2): string => {
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(num)) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
};

export const formatCrypto = (
  amount: number | string,
  symbol: string,
): string => {
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(num)) return `0 ${symbol}`;
  const decimals = ["BTC", "ETH"].includes(symbol)
    ? 6
    : ["XRP", "ADA", "DOGE"].includes(symbol)
      ? 2
      : 4;
  return `${num.toFixed(decimals)} ${symbol}`;
};

export const formatPercent = (
  val: number | string,
  showSign = true,
): string => {
  const num = typeof val === "string" ? parseFloat(val) : val;
  if (isNaN(num)) return "0.00%";
  const sign = showSign && num > 0 ? "+" : "";
  return `${sign}${num.toFixed(2)}%`;
};

export const formatPnL = (val: number | string): string => {
  const num = typeof val === "string" ? parseFloat(val) : val;
  if (isNaN(num)) return "$0.00";
  // Show + for positive, - for negative (formatUSD of a negative number already includes -)
  if (num > 0) return `+${formatUSD(num)}`;
  if (num < 0) return formatUSD(num); // Intl.NumberFormat already adds the minus sign
  return "$0.00";
};

export const formatDate = (dateStr: string): string => {
  try {
    return format(new Date(dateStr), "MMM d, yyyy HH:mm");
  } catch {
    return dateStr;
  }
};

export const formatRelativeDate = (dateStr: string): string => {
  try {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
  } catch {
    return dateStr;
  }
};

export const formatCompact = (val: number): string => {
  if (val >= 1e9) return `$${(val / 1e9).toFixed(2)}B`;
  if (val >= 1e6) return `$${(val / 1e6).toFixed(2)}M`;
  if (val >= 1e3) return `$${(val / 1e3).toFixed(2)}K`;
  return formatUSD(val);
};

export const formatPrice = (price: number | string): string => {
  const num = typeof price === "string" ? parseFloat(price) : price;
  if (isNaN(num)) return "$0.00";
  if (num >= 1000) return formatUSD(num, 2);
  if (num >= 1) return formatUSD(num, 4);
  return formatUSD(num, 6);
};

export const truncateAddress = (addr: string, chars = 6): string => {
  if (!addr) return "";
  return `${addr.slice(0, chars)}...${addr.slice(-chars)}`;
};

export const pnlClass = (val: number | string): string => {
  const num = typeof val === "string" ? parseFloat(val) : val;
  if (num > 0) return "text-primary";
  if (num < 0) return "text-red-500";
  return "text-gray-400";
};
